import React, { useState, useEffect, createContext } from "react";
import { fetchData } from "./dataFetcher";
import Family from "./components/Family";
import About from './components/About';
import SearchBox from './components/SearchBox';
import QueryGenerator from './components/QueryGenerator';
import "./App.css";

export const ThemeContext = createContext();

function App() {
  const [data, setData] = useState([]);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); 
  const [highlightedValues, setHighlightedValues] = useState("");
  const [checkedTables, setCheckedTables] = useState({});
  const [query, setQuery] = useState(""); 
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [tableFields, setTableFields] = useState({});

  useEffect(() => {
    fetchData().then((fetchedData) => {
      setData(fetchedData);
    });
  }, []);

  useEffect(() => {
    fetchData().then(fetchedData => {
      const transformedData = fetchedData.reduce((acc, table) => {
        acc[table.name] = table.fields.map(field => field.field);
        return acc;
      }, {});
      setTableFields(transformedData); // Use only transformed data
    });
  }, []);

  useEffect(() => {
    const prefersDarkScheme = window.matchMedia("(prefers-color-scheme: dark)");
    if (prefersDarkScheme.matches) {
      setIsDarkMode(true);
      document.body.classList.add('dark-mode'); 
    }
  }, []);

  const groupedData = data.reduce((result, item) => {
    (result[item.family] = result[item.family] || []).push(item);
    return result;
  }, {});

  const fieldsMap = data.reduce((acc, table) => {
    table.fields.forEach(field => {
      const fieldName = field.field;
      acc[fieldName] = (acc[fieldName] || 0) + 1;
    });
    return acc;
  }, {});

  const fieldsData = Object.entries(fieldsMap).map(([name, count]) => ({ name, count }));

  const handleSearch = (value) => {
    setSearchTerm(value);
    setHighlightedValues(value);
  };

  const filteredGroupedData = Object.entries(groupedData).reduce((acc, [family, tables]) => {
    const filteredTables = tables.filter(table => 
      table.fields.some(field => field.field.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    if (filteredTables.length > 0) {
      acc[family] = filteredTables;
    }
    return acc;
  }, {});

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.body.classList.toggle('dark-mode');
  };

  const handleToggleCheckbox = (tableName, isChecked) => {
    setCheckedTables(prev => ({
      ...prev,
      [tableName]: isChecked,
    }));
  };

  const updateQuery = (newQuery) => {
    setQuery(newQuery);
  };

  const handleGenerateQuery = () => {
    navigator.clipboard.writeText(query).then(() => {
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 3000); // Hide message after 3 seconds
    });
  };
  
  
  const isGenerateButtonDisabled = !Object.values(checkedTables).some(isChecked => isChecked);

  const [specialJoinApplied, setSpecialJoinApplied] = useState(false);

  function handleSpecialJoinStatus(isApplied) {
    setSpecialJoinApplied(isApplied);
  }

  return (
    <ThemeContext.Provider value={{ isDarkMode }}>
      <div className={`App ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <About isDarkMode={isDarkMode} className="about-margin-bottom" />
        <div className="toggles-container">
          <div className="toggles">
            <label>
              <span>Display details</span>
              <div className="switch">
                <input type="checkbox" onChange={toggleZoom} />
                <span className="slider"></span>
              </div>
            </label>
            <label>
              <span>Dark mode</span>
              <div className="switch">
                <input type="checkbox" checked={isDarkMode} onChange={toggleDarkMode} />
                <span className="slider"></span>
              </div>
            </label>
          </div>
          <SearchBox fields={fieldsData} onSearch={handleSearch} style={{ flexGrow: 1 }} />
          <div style={{ paddingLeft: '1rem', paddingBottom: '1rem' }}>
  <button
    disabled={isGenerateButtonDisabled} 
    onClick={handleGenerateQuery}
    className={`generate ${isGenerateButtonDisabled ? "disabled-button" : ""}`}
  >
    Generate Query
  </button>
</div>
{showCopySuccess && (
  <div className="toast-notification">
    Query has been saved to clipboard. 
    {specialJoinApplied ? " NOTE: Additional tables added for valid joins due to missing common fields." : ""}
  </div>
)}
<QueryGenerator 
  checkedTables={checkedTables}
  updateQuery={setQuery}
  tableStructures={tableFields}
  onSpecialJoinApplied={handleSpecialJoinStatus}
/>
</div>
        <div className="families">
          {Object.entries(filteredGroupedData).map(([family, tables]) => (
            <Family
              key={family}
              family={family}
              tables={tables}
              isZoomed={isZoomed}
              highlightedValues={highlightedValues}
              updateHighlightedValues={setHighlightedValues}
              onToggleCheckbox={handleToggleCheckbox}
            />
          ))}
        </div>
        <br />
        <footer>
          ver 1.217 &copy; {new Date().getFullYear()} Zuzanna Jarczynska. All rights reserved.
        </footer>
      </div>
    </ThemeContext.Provider>
  );
}

export default App;
